import { useRubro } from "./rubro";
import { Reveal } from "./Reveal";

// ---------------------------------------------------------------------------
// PROBLEM — "El trabajo ya lo hiciste. Se pierde en el camino."
// Four problem cards, each with a small visual mock of the situation.
// Cards stagger in on scroll.
// ---------------------------------------------------------------------------

export function HomeProblem() {
  const { rb } = useRubro();

  const months = Array.from({ length: 12 }, (_, i) =>
    i === 11 ? "rgba(232,121,249,0.5)" : "#1D1A26",
  );

  return (
    <section
      data-screen-label="Problema"
      className="mx-auto px-6"
      style={{
        maxWidth: 1240,
        paddingTop: "clamp(80px,10vw,140px)",
        paddingBottom: "clamp(80px,10vw,140px)",
      }}
    >
      <Reveal>
        <div
          className="font-mono text-[13px] text-[#A78BFA]"
          style={{ letterSpacing: "0.08em" }}
        >
          01 — EL PROBLEMA
        </div>
        <h2
          className="mt-5 text-[#EEEDF2] font-medium"
          style={{
            fontSize: "clamp(40px,5.6vw,76px)",
            lineHeight: 1,
            letterSpacing: "-0.025em",
            maxWidth: 980,
            textWrap: "balance" as never,
          }}
        >
          El trabajo ya lo hiciste.{" "}
          <span className="text-[#6E6A7A]">Se pierde en el camino.</span>
        </h2>
      </Reveal>

      <div
        className="mt-16 grid gap-px bg-[#221F2A] border border-[#221F2A] rounded-2xl overflow-hidden"
        style={{
          gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,210px),1fr))",
        }}
      >
        {/* 01 — No te encuentran */}
        <Reveal delay={0.05}>
          <ProblemCard
            code="01 · NO TE ENCUENTRAN"
            title="En Google gana el que tiene más reseñas."
            text="No el que trabaja mejor."
            visual={
              <div className="flex flex-col gap-1.5 p-3 overflow-hidden">
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-[#17141F] text-[11.5px] text-[#9C98A6] w-max">
                  <span className="font-bold text-[#BAB6C4]">G</span>
                  {rb.label} cerca de mí
                </div>
                <GoogleRow
                  n="1"
                  name="Competencia A"
                  stars="★ 4,9"
                  count="(212)"
                />
                <GoogleRow
                  n="2"
                  name="Competencia B"
                  stars="★ 4,8"
                  count="(140)"
                />
                <div
                  className="mt-auto grid items-center rounded-lg border border-dashed border-[#E879F9] px-2 py-1.5 text-[12px]"
                  style={{ gridTemplateColumns: "18px 1fr auto", gap: 8 }}
                >
                  <span className="text-[#E879F9] font-mono">7</span>
                  <span className="font-semibold text-[#EEEDF2]">Vos</span>
                  <span className="text-[#9C98A6]">★ 4,2 (9)</span>
                </div>
              </div>
            }
          />
        </Reveal>

        {/* 02 — No vuelven */}
        <Reveal delay={0.12}>
          <ProblemCard
            code="02 · NO VUELVEN"
            title="Clientes a los que nunca volviste a escribir."
            text={`Nadie les avisa que toca ${rb.rec}.`}
            visual={
              <div className="flex flex-col justify-center gap-3 p-3.5 overflow-hidden">
                <div className="flex items-center gap-2.5">
                  <div
                    className="rounded-full bg-[#1D1A26] grid place-items-center font-semibold text-[#BAB6C4]"
                    style={{ width: 34, height: 34, fontSize: 12 }}
                  >
                    MR
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold text-[#EEEDF2]">
                      María R.
                    </div>
                    <div className="text-[11.5px] text-[#9C98A6]">
                      Cliente · {rb.job}
                    </div>
                  </div>
                </div>
                <div className="flex justify-between items-center px-3 py-2.5 rounded-[10px] bg-[#17141F]">
                  <span className="text-[12px] text-[#9C98A6]">
                    Último contacto
                  </span>
                  <span className="text-[13px] font-semibold text-[#E879F9]">
                    hace 12 meses
                  </span>
                </div>
                <div className="flex gap-[3px]">
                  {months.map((m, i) => (
                    <div
                      key={i}
                      className="flex-1 rounded-[3px]"
                      style={{ height: 18, background: m }}
                    />
                  ))}
                </div>
              </div>
            }
          />
        </Reveal>

        {/* 03 — No se cierra */}
        <Reveal delay={0.19}>
          <ProblemCard
            code="03 · NO SE CIERRA"
            title="El presupuesto que nadie volvió a mirar."
            text="Ese trabajo ya lo hiciste y no lo cobraste."
            visual={
              <div className="flex flex-col gap-2 p-3.5 overflow-hidden">
                <div className="self-end max-w-[85%] px-3 py-2 rounded-[10px_10px_2px_10px] bg-[#2A1F4A] text-[12px] leading-[1.35] text-[#E9E1FF]">
                  Te paso el presupuesto 📎
                  <div className="text-right text-[10px] text-[#A78BFA] mt-0.5">
                    ✓✓ 10:14
                  </div>
                </div>
                <div className="self-start max-w-[85%] px-3 py-2 rounded-[10px_10px_10px_2px] bg-[#1D1A26] text-[12px] text-[#D8D5E0]">
                  Lo veo y te aviso 👍
                </div>
                <div className="mt-auto flex items-center gap-2 text-[11.5px] text-[#6E6A7A]">
                  <div className="flex-1 h-px bg-[#2A2633]" />
                  <span className="text-[#E879F9] font-semibold">
                    21 días sin respuesta
                  </span>
                  <div className="flex-1 h-px bg-[#2A2633]" />
                </div>
              </div>
            }
          />
        </Reveal>

        {/* 04 — Llegás tarde */}
        <Reveal delay={0.26}>
          <ProblemCard
            code="04 · LLEGÁS TARDE"
            title="La consulta que se contestó tarde."
            text="El que contesta primero se lleva el trabajo."
            visual={
              <div className="flex flex-col gap-2.5 p-3.5 overflow-hidden">
                <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-[10px] bg-[#17141F]">
                  <div
                    className="rounded-[8px] bg-[#1D1A26] grid place-items-center font-mono font-semibold text-[#BAB6C4]"
                    style={{ width: 28, height: 28, fontSize: 10 }}
                  >
                    WA
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[12px] font-semibold text-[#EEEDF2]">
                      Nueva consulta
                    </div>
                    <div className="text-[11px] text-[#9C98A6]">
                      Domingo 22:40
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 px-1.5">
                  <div
                    className="ml-[13px]"
                    style={{
                      width: 2,
                      height: 22,
                      background:
                        "repeating-linear-gradient(#433C52 0 3px,transparent 3px 6px)",
                    }}
                  />
                  <span className="text-[11.5px] text-[#E879F9] font-semibold">
                    11 horas después
                  </span>
                </div>
                <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-[10px] border border-dashed border-[#433C52]">
                  <div
                    className="rounded-[8px] bg-[#1D1A26] grid place-items-center text-[#9C98A6]"
                    style={{ width: 28, height: 28, fontSize: 12 }}
                  >
                    ↩
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[12px] font-semibold text-[#9C98A6]">
                      Respondido
                    </div>
                    <div className="text-[11px] text-[#6E6A7A]">
                      Lunes 09:50 · ya compró en otro lado
                    </div>
                  </div>
                </div>
              </div>
            }
          />
        </Reveal>
      </div>
    </section>
  );
}

function GoogleRow({
  n,
  name,
  stars,
  count,
}: {
  n: string;
  name: string;
  stars: string;
  count: string;
}) {
  return (
    <div
      className="grid items-center rounded-lg bg-[#17141F] px-2 py-1.5 text-[12px]"
      style={{ gridTemplateColumns: "18px 1fr auto", gap: 8 }}
    >
      <span className="text-[#6E6A7A] font-mono">{n}</span>
      <span className="text-[#EEEDF2]">{name}</span>
      <span className="text-[#F2B65A]">
        {stars} <span className="text-[#9C98A6]">{count}</span>
      </span>
    </div>
  );
}

function ProblemCard({
  code,
  title,
  text,
  visual,
}: {
  code: string;
  title: string;
  text: string;
  visual: React.ReactNode;
}) {
  return (
    <div className="bg-[#0F0D14] p-6 flex flex-col gap-3.5 hover:bg-[#17141F] transition-colors h-full">
      <div
        className="rounded-xl bg-[#0A090D] border border-[#221F2A] overflow-hidden"
        style={{ height: 168 }}
      >
        {visual}
      </div>
      <div
        className="font-mono text-[11.5px] text-[#E879F9]"
        style={{ letterSpacing: "0.06em" }}
      >
        {code}
      </div>
      <h3
        className="text-[18px] font-semibold tracking-[-0.01em] text-[#EEEDF2]"
        style={{ textWrap: "balance" as never }}
      >
        {title}
      </h3>
      <p
        className="text-[#9C98A6] leading-[1.5] text-[14.5px]"
        style={{ textWrap: "pretty" as never }}
      >
        {text}
      </p>
    </div>
  );
}
